import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, BookOpen, Download, Navigation, ShieldCheck, Building2 } from 'lucide-react';
import { FadeUp } from './MotionReveals';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnnuaireHomeSection() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/annuaire/stats`)
      .then((r) => r.json())
      .then((d) => setCount(d.count || null))
      .catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24" data-testid="annuaire-home-section">
      <FadeUp>
        <div className="flex items-end justify-between gap-4 mb-5 sm:mb-7">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono-spec text-red-400 font-bold mb-2">Nouveau sur Code Voiture</div>
            <h2 className="font-serif-editorial text-white text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.05]">
              L'annuaire officiel <span className="text-red-500">&amp;</span> le livret AM
            </h2>
          </div>
        </div>
      </FadeUp>

      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-4 sm:gap-5">
        {/* ── Carte Annuaire ── */}
        <FadeUp>
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-red-800/30 bg-gradient-to-br from-[#1a0b0d] via-[#120a0b] to-[#0d0d0e] p-5 sm:p-8 h-full flex flex-col">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/10 blur-3xl pointer-events-none" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-red-300 text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-4 self-start">
              <MapPin size={12} /> Annuaire national · data.gouv.fr
            </div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[30px] leading-tight">
              Trouve ton auto-école parmi{' '}
              <span className="text-red-400">{count ? count.toLocaleString('fr-FR') : '11 000+'}</span> écoles agréées
            </h3>
            <p className="text-white/60 text-[13px] sm:text-[14.5px] mt-3 max-w-xl leading-relaxed">
              Carte de France interactive, recherche par ville ou code postal, géolocalisation « autour de moi »,
              Label Qualité et taux de réussite au permis B. Données officielles Sécurité Routière.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { icon: Navigation, t: 'Autour de moi' },
                { icon: Building2, t: '104 départements' },
                { icon: ShieldCheck, t: 'Permis B · AM · B96 · BE' },
              ].map(({ icon: Icon, t }, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[11.5px] font-semibold">
                  <Icon size={12} className="text-red-400" /> {t}
                </span>
              ))}
            </div>
            <div className="mt-6 sm:mt-auto pt-5 flex flex-wrap gap-2 sm:gap-3">
              <Link to="/annuaire" data-testid="home-annuaire-cta" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40 transition-colors">
                <MapPin size={15} /> Ouvrir l'annuaire + carte <ArrowRight size={14} />
              </Link>
              <Link to="/auto-ecoles" data-testid="home-annuaire-depts" className="h-11 px-5 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5 inline-flex items-center gap-2 transition-colors">
                Parcourir par département
              </Link>
            </div>
          </div>
        </FadeUp>

        {/* ── Carte Livret AM ── */}
        <FadeUp delay={0.08}>
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-amber-600/30 bg-gradient-to-br from-[#171004] via-[#110d06] to-[#0d0d0e] p-5 sm:p-8 h-full flex flex-col">
            <div className="absolute -bottom-20 -right-16 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-4">
                  <BookOpen size={12} /> Voiturette · dès 14 ans
                </div>
                <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] leading-tight">
                  Livret AM Topolino<br /><span className="text-amber-300">« Mission 8 h »</span>
                </h3>
                <p className="text-white/60 text-[13px] sm:text-[14px] mt-3 leading-relaxed">
                  67 pages pédagogiques à feuilleter en ligne : flipbook interactif avec zoom,
                  signets, recherche plein texte — ou PDF à télécharger.
                </p>
              </div>
              <img
                src="/livrets/am-8h-2026/pages/p1.jpg"
                alt="Couverture du livret AM Topolino"
                loading="lazy"
                className="hidden sm:block w-24 lg:w-28 rounded-lg border border-white/15 shadow-xl shadow-black/50 rotate-2 shrink-0"
              />
            </div>
            <div className="mt-6 sm:mt-auto pt-5 flex flex-wrap gap-2 sm:gap-3">
              <Link to="/livret-am/lecteur" data-testid="home-livret-am-cta" className="h-11 px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-amber-900/40 transition-colors">
                <BookOpen size={15} /> Lire en ligne (flipbook)
              </Link>
              <a
                href="/livrets/am-8h-2026/livret-am-topolino-2026.pdf"
                download="Livret-AM-Topolino-Mission-8H-2026.pdf"
                data-testid="home-livret-am-pdf"
                className="h-11 px-5 rounded-full border border-amber-500/40 text-amber-300 text-[13px] sm:text-[14px] font-medium hover:bg-amber-950/20 inline-flex items-center gap-2 transition-colors"
              >
                <Download size={14} /> PDF
              </a>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
